const assert = require('assert');
const fell = require('@dbk-legacy/fell');
const fromPairs = require('lodash/fromPairs');
const noop = require('lodash/noop');
const { isRef, joinPartialPaths } = require('../../muster-utils');

const Router = require('./router');
const Store = require('./store');
const {
  addPathPrefix,
  join,
  split,
  parseRef,
  isDynamicRoute,
  RouteMatcher,
} = require('../../muster-utils');
const {
  parseRouteHandler,
  prefixRouteHandlerPaths,
  createAliasHandler,
} = require('./route-handler');

/**
 * Muster App
 *
 * The muster app is used to define a set of valid Muster routes that correspond to points in the
 * Muster data graph.
 *
 * Muster apps specify route configuration, and can be used to create app instances which can be
 * used to handle data operations.
 */
class Muster {
  /**
   * A string describing a route to a specific leaf in the graph.
   * All Path strings are valid Falcor path syntax
   * [Path syntax strings](https://netflix.github.io/falcor/documentation/paths.html#path-syntax-strings).
   * @typedef {string} Path
   * @example
   * 'ui.history.currentPath'
   * 'fx.pairs[6].ask'
   */

  /**
   * A string describing a route to (potentially multiple) leaves in the graph. PathSet syntax is a
   * superset of the Path syntax, and can contain range expressions (e.g. `[0..20]`) and property
   * arrays (e.g. `["bid","ask","name","type"]`).
   * All PathSet strings are valid Falcor path syntax
   * [PathSet syntax strings](https://netflix.github.io/falcor/documentation/paths.html#pathset-syntax-string).
   * @typedef {Path} PathSet
   * @example
   * 'ui.history.currentPath'
   * 'fx.pairs[0..10].bid'
   * 'fx.pairs[0..10].["bid","ask","name","type"]'
   */

  /**
   * A string describing a route pattern to match queries against. When used to declare a route
   * handler, any wildcard segments will be extracted into the `req.params` object that is passed
   * into the route handler.
   * All RoutePattern strings are valid Falcor Router
   * [route patterns](https://netflix.github.io/falcor/documentation/paths.html#route-pattern-matching).
   * @typedef {string} RoutePattern
   * @example
   * 'fx.pairs[0].bid'
   * 'fx.pairs[{integers}].bid'
   * 'fx.pairs[{integers:id}].bid'
   * 'fx.pairs[{integers:id}].["bid","ask","name","type"]'
   * 'fx.pairs[{integers:id}].[{keys}]'
   * 'fx.pairs[{integers:id}].[{keys:field}]'
   */

  /**
   * An object describing which methods a route dependency must implement
   * @typedef {GetHandlerFunction|MethodRouteHandlerDefinition|RouteHandler} RouteHandlerDefinition
   */

  /* eslint-disable max-len */
  /**
   * Get handler function
   * @typedef {function(requests:Array<GetRequest>,callbacks:GetHandlerCallbacks):Array<PathValue>} GetHandlerFunction
   */

  /**
   * Set handler function
   * @typedef {function(requests:Array<SetRequest>,callbacks:SetHandlerCallbacks):void|Promise<void>} SetHandlerFunction
   */

  /**
   * Call handler function
   * @typedef {function(requests:Array<CallRequest>,callbacks:CallHandlerCallbacks):CallHandlerResponse|Promise<CallHandlerResponse>} CallHandlerFunction
   */
  /* eslint-enable max-len */

  /**
   * Call hander function response
   * @typedef {Object} CallHandlerResponse
   * @property {Array<PathValue>} values Array of updated/invalidated path values
   */

  /**
   * Get request metadata object (passed into get handler functions)
   * @typedef {Object} GetRequest
   * @property {Object} [req] Query metadata
   * @property {String} [req.path] Query path
   * @property {Object} [req.params] Query parameters (object containing named keys for named
   * parameters and numerical keys for unnamed parameters)
   * @property {Object} [res] Response utility methods
   * @property {function(value:any):PathValue} [res.value] Pre-bound path value factory
   * @property {function(message:string):PathValue} [res.error] Pre-bound path error factory
   * @property {function():PathValue} [res.notFound] Pre-bound missing path factory
   */

  /**
   * Get requery callbacks (passed into get handler functions)
   * @typedef {Object} GetHandlerCallbacks
   * @property {function} get App instance get() method, pre-bound to the module path
   */

  /**
   * Set request metadata object (passed into set handler functions)
   * @typedef {Object} SetRequest
   * @property {Object} [req] Query metadata
   * @property {String} [req.path] Query path
   * @property {Object} [req.params] Query parameters (object containing named keys for named
   * parameters and numerical keys for unnamed parameters)
   * @property {any} [req.value] Query value
   */

  /**
   * Set requery callbacks (passed into set handler functions)
   * @typedef {Object} SetHandlerCallbacks
   * @property {function} get App instance get() method, pre-bound to the module path
   * @property {function} set App instance set() method, pre-bound to the module path
   */

  /**
   * @typedef {Object} CallRequest
   * @property {Object} [req] Query metadata
   * @property {String} [req.path] Query path
   * @property {Object} [req.params] Query parameters (object containing named keys for named
   * parameters and numerical keys for unnamed parameters)
   * @property {any} [req.value] Query value
   */

  /**
   * Call requery callbacks (passed into call handler functions)
   * @typedef {Object} CallHandlerCallbacks
   * @property {function} get App instance get() method, pre-bound to the module path
   * @property {function} set App instance set() method, pre-bound to the module path
   * @property {function} call App instance call() method, pre-bound to the module path
   */

  /**
   * @typedef {Object} MethodRouteHandlerDefinition
   * @property {GetHandlerFunction} [get] Get method handler
   * @property {SetHandlerFunction} [set] Set method handler
   * @property {CallHandlerFunction} [call] Call method handler
   */

  /**
   * An object describing which methods a route dependency must implement
   * @typedef {Object} RouteDependencyMethods
   * @property {boolean} get Whether the provided route must specify a get handler
   * @property {boolean} set Whether the provided route must specify a set handler
   * @property {boolean} call Whether the provided route must specify a call handler
   */

  /**
   * An object describing a reference to a leaf in the graph
   * @typedef {Object} PathRef
   * @property {string} $type The object type identifier. This will always be "ref".
   * @property {Path} value Path to a leaf in the graph
   */

  /**
   * An object containing configuration options for creating a Muster app instance
   * @typedef {Object} AppInstanceConfigurationOptions
   * @property {Store} [store] Custom Muster store instance
   * @property {Object|function} [log] Custom log configuration
   * @property {function} [log.debug] Custom debug-level log function
   * @property {function} [log.info] Custom info-level log function
   * @property {function} [log.warn] Custom warning-level log function
   */

  /**
   * Create a new Muster instance
   */
  constructor() {
    this.routes = new RouteMatcher();
    this.dependencies = new RouteMatcher();
  }

  /**
   * Define a static route handler
   * @param {Path} path Path to match against
   * @param {RouteHandlerDefinition|RouteHandler} handler Route handler definition
   * @returns {Muster} The current Muster instance
   * @example
   * const appName = 'foobar';
   * const currentUserId = 'johndoe';
   * const app = muster()
   *   .path('ui.appName', ({ res }) => res.value(appName))
   *   .path('ui.currentUserId', ({ res }) => res.value(currentUserId));
   */
  path(path, handler) {
    assert(!isDynamicRoute(path),
      `Dynamic route pattern supplied for ${formatPath(path)} route handler - use .match() instead`
    );
    const routeHandler = isRef(handler) ?
      createAliasPathHandler(handler, path, {
        routes: this.routes,
        dependencies: this.dependencies,
      }) :
      parseRouteHandler(handler, path);
    this.routes.set(path, routeHandler);
    return this;
  }

  /**
   * Define a dynamic route handler
   * @param {RoutePattern|Array<RoutePattern>} pattern Route pattern(s) to match against
   * @param {RouteHandlerDefinition|RouteHandler} handler Route handler definition
   * @returns {Muster} The current Muster instance
   * @example
   * const users = { '0': { username: 'johndoe' }, '1': { username: 'janedoe' } };
   * const app = muster()
   *   .route('users[{integers:id}].username', (requests) =>
   *     requests.map(({ req, res }) => res.value(users[req.params.id].username))
   *   )
   *   .route('users[{integers:id}].id', (requests) =>
   *     requests.map(({ req, res }) => res.value(req.params.id))
   *   );
   */
  match(pattern, handler) {
    assert(isDynamicRoute(pattern),
      `Static path supplied for ${formatPath(pattern)} route handler - use .path() instead`
    );
    const patterns = Array.isArray(pattern) ? pattern : [pattern];
    const routeHandler = parseRouteHandler(handler, pattern);
    patterns.forEach((routePattern) => {
      this.routes.set(routePattern, routeHandler);
    });
    return this;
  }

  /**
   * Define a module dependency
   * @param {Path} path Required dependency path
   * @param {RouteDependencyMethods} methods Required dependency methods
   * @returns {Muster} The current Muster instance
   * @throws If no path is specified
   * @throws If no dependency methods are specified
   * @throws If a dependency has already been defined for the specified path
   * @example
   * const authModule = muster()
   *   .require('userId', { get: true, set: true })
   *   .require('changePage', { call: true })
   *   .route('login', {...})
   *   .route('logout', {...});
   */
  require(path, methods) {
    assert(path, 'Missing require() dependency path');
    assert(methods, `Missing required dependency methods for ${formatPath(path)}`);
    assert(!this.dependencies.has(path),
      `Dependency definition already exists for ${formatPath(path)}`
    );
    const dependencyMethods = {
      get: (methods.get === true),
      set: (methods.set === true),
      call: (methods.call === true),
      '*': (methods['*'] === true),
    };
    assert(!(methods['*'] && (methods.get || methods.set || methods.call)),
      `Invalid combination of dependency methods specified for ${formatPath(path)}` +
      ' - "*" cannot be combined with "get", "set" or "call"'
    );
    assert(methods.get || methods.set || methods.call || methods['*'],
      `No dependency methods specified for ${formatPath(path)}`
    );
    this.dependencies.set(path, dependencyMethods);
    return this;
  }

  /**
   * Import routes from another Muster modue
   * @param {Path} prefix Graph branch at which to append the imported routes
   * @param {Muster} module Imported Muster module
   * @param {Object<string,PathRef>} [dependencies] References to the target route dependencies
   * required by the imported module
   * @returns {Muster} The current Muster instance
   * @example
   * const authModule = muster()
   *   .require('userId', { get: true, set: true })
   *   .require('changePage', { call: true })
   *   .route('login', ...)
   *   .route('logout', ...);
   * const app = muster()
   *   .route('ui.activeUserId', { get: () => {...}, set: () => {...} })
   *   .route('ui.history.push', { call: () => {...} })
   *   .import('auth', authModule, {
   *     userId: ref('ui.activeUserId'),
   *     changePage: ref('ui.history.push'),
   *   });
   */
  import(prefix, module, dependencies) {
    // Create alias routes for the injected module's dependencies
    const injectedRoutes = resolveModuleDependencies(module, dependencies, {
      prefix,
      routes: this.routes,
      dependencies: this.dependencies,
    });

    // Create prefixed route handers from the imported module
    const importedRoutes = createPrefixedModuleRoutes(module, { prefix });

    // Add the imported routes to the current Muster instance
    importedRoutes.concat(injectedRoutes).forEach(({ pattern, handler }) => {
      this.routes.set(pattern, handler);
    });

    return this;
  }

  /**
   * Create a Muster app instance from the current Muster app route definitions
   * @param {AppInstanceConfigurationOptions} [options] Muster app instance configuration options
   * @returns {Router} Muster app instance
   * @example
   * const app = muster()
   *   .route('greet', ({ res }) => res.value('Hello, world!'));
   * const appInstance = app.run();
   * appInstance.get('greet').subscribe(([{ value }]) => console.log(value));
   */
  run(options = {}) {
    // Ensure that all the module's dependencies have been satisfied before initialising
    validateModuleDependencies(this);

    // Create a new router instance for the module routes
    const routes = fromPairs(this.routes.entries());

    const router = new Router(routes, Object.assign({}, options, {
      store: options.store || new Store({
        log: getLogger(options.log, 'muster.Store'),
      }),
      log: getLogger(options.log, 'muster.Router'),
    }));

    /* global window */
    const globalObject = global || window;
    /* eslint-disable no-underscore-dangle */
    if (globalObject.__MUSTER_GLOBAL_HOOK__) {
      // The devtools set up a global hook, if it's available then send the router
      // instance to it.
      globalObject.__MUSTER_GLOBAL_HOOK__.addMuster(router);
    }
    /* eslint-enable no-underscore-dangle */

    return router;

    function getLogger(log, defaultLogNamespace) {
      if (log === false) {
        return { debug: noop, info: noop, warn: noop, error: noop };
      }
      if (typeof log === 'function') {
        return { debug: log, info: log, warn: log, error: log };
      }
      return log || fell.getLogger(defaultLogNamespace);
    }
  }
}

module.exports = Muster;

function validateModuleDependencies(module) {
  module.dependencies.entries().forEach(([path, dependencyMethods]) => {
    Object.keys(dependencyMethods)
      .filter((method) => dependencyMethods[method] === true)
      .forEach((method) => {
        const targetRoute = module.routes.get(path);
        const hasMethodHandler = Boolean(targetRoute && targetRoute[method]);
        assert(hasMethodHandler, `Missing ${method} handler dependency for ${formatPath(path)}`);
      });
  });
}

function resolveModuleDependencies(module, injectedDependencies, {
  prefix,
  routes,
  dependencies,
}) {
  // Check that no superfluous dependencies were provided
  const requiredDependencies = module.dependencies.keys();
  const suppliedDependencies = injectedDependencies ? Object.keys(injectedDependencies) : [];
  suppliedDependencies.forEach((routePattern) => {
    assert(requiredDependencies.includes(routePattern),
      `Unrecognised route handler dependency passed to module ${formatPath(prefix)}:` +
      ` ${formatPath(routePattern)}`
    );
  });
  // Build an array of { pattern, handler } objects mapping routes to alias handlers
  return module.dependencies.entries()
    .map(([routePattern, dependencyMethods]) => {
      assert(injectedDependencies && injectedDependencies[routePattern],
        `Missing route handler dependency for module ${formatPath(prefix)}:` +
        ` ${formatPath(routePattern)}`
      );
      const targetRef = injectedDependencies[routePattern];
      assert(isRef(targetRef),
        `Invalid route handler dependency for module ${formatPath(prefix)}: ` +
        `${formatPath(routePattern)}` +
        ` - expected Ref, received ${injectedDependencies[routePattern]}`
      );
      const targetPath = (() => {
        try { return parseRef(targetRef); } catch (error) { return null; }
      })();
      assert(targetPath,
        `Invalid route handler dependency for module ${formatPath(prefix)}: ` +
        `${formatPath(routePattern)}` +
        ` - invalid Ref path: ${targetRef.value}`
      );
      const targetRouteExists = routes.has(targetPath) || dependencies.has(targetPath);
      assert(targetRouteExists,
        `Missing route handler dependency for module ${formatPath(prefix)}: ` +
        `${formatPath(routePattern)}` +
        ` - ${targetPath} does not exist`
      );
      if (dependencyMethods['*'] === true) {
        return createWildcardDependencyHandler(routePattern, targetPath, {
          prefix,
        });
      }
      return createPathDependencyHandler(routePattern, targetPath, dependencyMethods, {
        prefix,
        routes,
        dependencies,
      });
    });
}

function createAliasPathHandler(targetRef, path, { routes, dependencies }) {
  const targetPath = parseRef(targetRef);
  const targetHandler = routes.get(targetPath) || dependencies.get(targetPath);
  assert(targetHandler,
    `Missing target handler for alias at ${formatPath(path)}:` +
    ` ${formatPath(targetPath)}`
  );
  const handlerMethods = fromPairs(
    ['get', 'set', 'call']
      .filter((method) => targetHandler[method])
      .map((method) => [method, true])
  );
  return createAliasHandler(targetPath, handlerMethods);
}

function createPrefixedModuleRoutes(module, { prefix }) {
  return module.routes.entries()
    .map(([routePattern, handler]) => {
      const prefixedPattern = addPathPrefix(routePattern, prefix);
      const prefixedHandler = prefixRouteHandlerPaths(handler, prefix);
      return { pattern: prefixedPattern, handler: prefixedHandler };
    });
}

function createPathDependencyHandler(routePattern, targetPath, dependencyMethods, {
  prefix,
  routes,
  dependencies,
}) {
  Object.keys(dependencyMethods)
    .filter((method) => dependencyMethods[method] === true)
    .forEach((method) => {
      const targetRoute = routes.get(targetPath) || dependencies.get(targetPath);
      const hasMethodHandler = Boolean(targetRoute && targetRoute[method]);
      assert(hasMethodHandler,
        `Missing ${method} handler dependency for module ${formatPath(prefix)}:` +
        ` ${formatPath(routePattern)}`
      );
    });
  const prefixedPath = addPathPrefix(routePattern, prefix);
  const aliasedHandler = createAliasHandler(targetPath, dependencyMethods);
  return { pattern: prefixedPath, handler: aliasedHandler };
}

function createWildcardDependencyHandler(routePattern, targetPath, {
  prefix,
}) {
  const prefixedPath = addPathPrefix(routePattern, prefix);
  const wildcardRoutePattern = join([...split(prefixedPath), '*']);
  const aliasedHandler = parseRouteHandler({
    get(requests) {
      return requests.map(
        ({ req, res }) => res.redirect(joinPartialPaths(targetPath, req.params['*']))
      );
    },
    set(requests) {
      return requests.map(
        ({ req, res }) => res.redirect(joinPartialPaths(targetPath, req.params['*']))
      );
    },
    call({ req }, { call }) {
      const targetCallPath = joinPartialPaths(targetPath, req.params['*']);
      return call(targetCallPath, ...req.args)
        .then((response) => ({ response }));
    },
  }, wildcardRoutePattern);
  return { pattern: wildcardRoutePattern, handler: aliasedHandler };
}

function formatPath(path) {
  return JSON.stringify(path).replace(/\\"/g, '"');
}
